'use client'

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { usePathname } from 'next/navigation'

// Types for chat messages
export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  toolCalls?: ToolCall[]
  isLoading?: boolean
}

export interface ToolCall {
  id: string
  name: string
  args: Record<string, unknown>
  result?: unknown
}

// Types for policies
export interface AIPolicy {
  id: string
  name: string
  description: string
  naturalLanguage: string
  dsl: PolicyDSL | null
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface PolicyDSL {
  conditions: PolicyCondition[]
  actions: PolicyAction[]
  match_mode?: 'all' | 'any'
  stop_on_match?: boolean
}

export interface PolicyCondition {
  field: string
  operator: 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'not_contains' | 'starts_with' | 'ends_with' | 'between' | 'in' | 'not_in' | 'matches' | 'is_null' | 'is_not_null'
  value: string | number | boolean | unknown[] | null
  field_path?: string
}

export interface PolicyAction {
  type: 'auto_approve' | 'auto_reject' | 'flag_review' | 'notify' | 'set_status' | 'set_field' | 'add_note' | 'add_tag' | 'escalate' | 'custom'
  value?: string | number | boolean | Record<string, unknown>
  params?: Record<string, unknown>
}

// Types for insights
export interface AIInsight {
  id: string
  type: 'pattern' | 'anomaly' | 'recommendation'
  severity: 'critical' | 'warning' | 'info'
  title: string
  description: string
  data?: Record<string, unknown>
  suggestedAction?: string
  createdAt: Date
}

// Types for call analysis context
export interface CallAnalysisTranscriptEntry {
  speaker: string
  text: string
  timestamp?: string
}

export interface CallAnalysisMetricScore {
  score: number
  label: string
  feedback?: string
  status?: 'pass' | 'fail'
  evidence?: string
  key?: string
}

export interface CallAnalysisKeyMoment {
  timestamp: string
  timestamp_seconds: number
  title: string
  description: string
  sentiment: 'positive' | 'neutral' | 'negative'
  moment_type: 'objection' | 'buying_signal' | 'question' | 'agreement' | 'concern' | 'highlight'
}

export interface CallAnalysisMissedOpportunity {
  title: string
  description: string
  impact: 'high' | 'medium' | 'low'
  recommendation: string
  timestamp?: string
}

export interface CallAnalysisFollowUpTask {
  task: string
  priority: 'high' | 'medium' | 'low'
  due_suggestion: string
  context: string
  assignee: 'sales_rep' | 'customer' | 'team'
}

export interface CallAnalysisCoachingRecommendation {
  area: string
  current_assessment: string
  score: number
  recommendation: string
  example_phrase?: string
  resources?: string[]
}

export interface CallAnalysisEnhancedInsights {
  call_summary: string
  call_outcome: 'positive' | 'neutral' | 'negative'
  customer_sentiment: 'very_positive' | 'positive' | 'neutral' | 'negative' | 'very_negative'
  customer_intent_score?: 'Very High' | 'High' | 'Moderate' | 'Low'
  key_topics: { topic: string; relevance: 'high' | 'medium' | 'low'; discussion_percentage: number }[]
  key_moments: CallAnalysisKeyMoment[]
  missed_opportunities: CallAnalysisMissedOpportunity[]
  follow_up_tasks: CallAnalysisFollowUpTask[]
  coaching_recommendations: CallAnalysisCoachingRecommendation[]
  strengths: string[]
  talk_ratio?: number
}

export interface CallAnalysisData {
  id: string
  filename: string
  status: string
  upload_date: string
  duration_seconds?: number
  transcript_data?: CallAnalysisTranscriptEntry[]
  metrics_data?: {
    overallScore?: CallAnalysisMetricScore
    checklist?: CallAnalysisMetricScore[]
    enhanced_insights?: CallAnalysisEnhancedInsights
    [key: string]: CallAnalysisMetricScore | CallAnalysisMetricScore[] | string[] | CallAnalysisEnhancedInsights | undefined
  }
}

// Context state type
interface AIContextState {
  // AI Manager state
  isManagerOpen: boolean
  openManager: () => void
  closeManager: () => void
  toggleManager: () => void
  
  // Chat state
  chatHistory: ChatMessage[]
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void
  clearHistory: () => void
  isTyping: boolean
  setIsTyping: (typing: boolean) => void
  
  // Page context
  currentPageContext: string
  
  // Call analysis context
  callAnalysisData: CallAnalysisData | null
  setCallAnalysisData: (data: CallAnalysisData | null) => void
  
  // Policies state
  policies: AIPolicy[]
  setPolicies: (policies: AIPolicy[]) => void
  addPolicy: (policy: AIPolicy) => void
  updatePolicy: (id: string, updates: Partial<AIPolicy>) => void
  removePolicy: (id: string) => void
  
  // Insights state
  insights: AIInsight[]
  setInsights: (insights: AIInsight[]) => void
  pendingInsightsCount: number
  markInsightAsRead: (id: string) => void
  
  // Suggestions
  hasPendingSuggestions: boolean
  setHasPendingSuggestions: (has: boolean) => void
}

const AIContext = createContext<AIContextState | undefined>(undefined)

export function useAI() {
  const context = useContext(AIContext)
  if (!context) {
    throw new Error('useAI must be used within an AIProvider')
  }
  return context
}

// Generate unique ID
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export function AIProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  
  // AI Manager state
  const [isManagerOpen, setIsManagerOpen] = useState(false)
  
  // Chat state
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([])
  const [isTyping, setIsTyping] = useState(false)
  
  // Call analysis context state
  const [callAnalysisData, setCallAnalysisData] = useState<CallAnalysisData | null>(null)
  
  // Policies state
  const [policies, setPolicies] = useState<AIPolicy[]>([])
  
  // Insights state
  const [insights, setInsights] = useState<AIInsight[]>([])
  const [readInsightIds, setReadInsightIds] = useState<Set<string>>(new Set())
  
  // Suggestions state
  const [hasPendingSuggestions, setHasPendingSuggestions] = useState(false)
  
  // Derive page context from pathname
  const currentPageContext = pathname || '/'
  
  // Clear call analysis data when navigating away from analyze pages
  useEffect(() => {
    if (!pathname?.startsWith('/analyze/') && callAnalysisData !== null) {
      setCallAnalysisData(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])
  
  // Manager controls
  const openManager = useCallback(() => setIsManagerOpen(true), [])
  const closeManager = useCallback(() => setIsManagerOpen(false), [])
  const toggleManager = useCallback(() => setIsManagerOpen(prev => !prev), [])
  
  // Chat controls
  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: generateId(),
      timestamp: new Date(),
    }
    
    // If this is a real assistant message (not loading), remove any existing loading messages first
    if (message.role === 'assistant' && !message.isLoading) {
      setChatHistory(prev => [...prev.filter(m => !m.isLoading), newMessage])
    } else {
      setChatHistory(prev => [...prev, newMessage])
    }
  }, [])
  
  const clearHistory = useCallback(() => {
    setChatHistory([])
  }, [])
  
  // Policy controls
  const addPolicy = useCallback((policy: AIPolicy) => {
    setPolicies(prev => [...prev, policy])
  }, [])
  
  const updatePolicy = useCallback((id: string, updates: Partial<AIPolicy>) => {
    setPolicies(prev => 
      prev.map(p => p.id === id ? { ...p, ...updates, updatedAt: new Date() } : p)
    )
  }, [])
  
  const removePolicy = useCallback((id: string) => {
    setPolicies(prev => prev.filter(p => p.id !== id))
  }, [])
  
  // Insights controls
  const pendingInsightsCount = insights.filter(i => !readInsightIds.has(i.id)).length
  
  const markInsightAsRead = useCallback((id: string) => {
    setReadInsightIds(prev => new Set([...prev, id]))
  }, [])
  
  // Keyboard shortcut for AI Manager (Cmd+J or Ctrl+J)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'j' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        toggleManager()
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [toggleManager])
  
  const value: AIContextState = {
    // Manager
    isManagerOpen,
    openManager,
    closeManager,
    toggleManager,
    
    // Chat
    chatHistory,
    addMessage,
    clearHistory,
    isTyping,
    setIsTyping,
    
    // Page context
    currentPageContext,
    
    // Call analysis context
    callAnalysisData,
    setCallAnalysisData,
    
    // Policies
    policies,
    setPolicies,
    addPolicy,
    updatePolicy,
    removePolicy,
    
    // Insights
    insights,
    setInsights,
    pendingInsightsCount,
    markInsightAsRead,
    
    // Suggestions
    hasPendingSuggestions,
    setHasPendingSuggestions,
  }
  
  return (
    <AIContext.Provider value={value}>
      {children}
    </AIContext.Provider>
  )
}

